/**
 * The owner's daily report — the "what happened yesterday" screen.
 *
 * This is the one analytics surface that names individual players and their money: how much each
 * staked, won or lost, deposited, and withdrew, and whether they walked away or kept playing. That
 * makes it the most sensitive read in the system — regulated player financial data — so it is
 * `admin`-only on the API and carries its own contract rather than riding the untyped passthrough
 * the other analytics routes use. A response that drifted from this shape fails its own parse and
 * becomes a 500 in the log rather than a wrong number in front of the owner.
 *
 * Every figure is a number of the account currency (USD). "GGR" here is gross gaming revenue *to the
 * house* — a positive `houseResult` means the house won money from that player that day, which is the
 * player losing. The screen frames it as the owner reads it ("we made / we lost"), but the field
 * names stay in house terms because that is what the warehouse computes.
 */
import { z } from 'zod';
/** A calendar date in the operator's reporting timezone, `YYYY-MM-DD`. */
export const reportDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe tener el formato YYYY-MM-DD.');
/**
 * The top-line numbers for the day, straight from `DAILY_STATS`. `netResult` is NGR — gross gaming
 * revenue after tax — and is the number the owner's first glance lands on: positive is a good day
 * for the house, negative is a day the house paid out more than it took.
 */
export const ownerHeadlineSchema = z.object({
    date: reportDateSchema,
    handle: z.number(),
    payout: z.number(),
    grossResult: z.number(),
    netResult: z.number(),
    tax: z.number(),
    activePlayers: z.number().int().nonnegative(),
    newPlayers: z.number().int().nonnegative(),
    /** GGR ÷ handle, the margin. A fraction (0.077 = 7.7%), not a percentage. */
    hold: z.number(),
});
/** One driver of the day's result — a sport/league line from `DAILY_STATS_BY_SPORT`. */
export const ownerDriverSchema = z.object({
    sport: z.string(),
    league: z.string().nullable(),
    turnover: z.number(),
    paidOut: z.number(),
    houseResult: z.number(),
});
/**
 * What a player did with their money on the day, in relation to the house.
 *
 * - `withdrew` — they took money out (a withdrawal was accepted that day).
 * - `kept_playing` — no withdrawal, and they still had betting activity; the money stayed in play.
 * - `deposited_more` — they put more money in and did not withdraw.
 * - `settled` — activity, but none of the above signals fired.
 * - `data_pending` — the deposit/withdrawal feed has not loaded for this day, so what the player did
 *   with their money is *unknown*, not "nothing". This is the honest status when the balance-events
 *   feed lags the bets feed (a known state after the Pliable migration): every player would otherwise
 *   fall through to `kept_playing`, which reads as a fact when it is really an absence of data.
 *
 * Derived server-side so the owner reads a word, not four columns of numbers they have to compare.
 */
export const playerDayStatuses = [
    'withdrew',
    'kept_playing',
    'deposited_more',
    'settled',
    'data_pending',
];
export const playerDayStatusSchema = z.enum(playerDayStatuses);
/**
 * One player's day. `label` is a deliberately minimal identifier — initials plus the White Hat
 * player id — not the full name and email, because the owner needs to recognise a whale, not run a
 * marketing export. The full identity lives in the source system for anyone with a reason to open it.
 */
export const ownerPlayerRowSchema = z.object({
    /** Initials, e.g. "A.V.". Never the full name on this surface. */
    label: z.string(),
    /** The White Hat player id, the stable handle for anyone who needs to look the player up. */
    playerRef: z.string(),
    category: z.string().nullable(),
    staked: z.number(),
    returned: z.number(),
    /** Positive means the house won from this player (the player lost). */
    houseResult: z.number(),
    betCount: z.number().int().nonnegative(),
    deposited: z.number(),
    withdrawn: z.number(),
    status: playerDayStatusSchema,
});
/** The whole report for one day: the headline, what drove it, and who drove it. */
export const ownerDailyReportSchema = z.object({
    date: reportDateSchema,
    /** Absent when `DAILY_STATS` has no row for the day yet (e.g. today, mid-day, before the run). */
    headline: ownerHeadlineSchema.nullable(),
    topDrivers: z.array(ownerDriverSchema),
    /** Players with activity that day, ordered by house result descending (biggest losers first). */
    players: z.array(ownerPlayerRowSchema),
    /**
     * Whether the deposit/withdrawal (balance-events) feed has loaded for this day at all.
     *
     * `false` means the feed carried zero rows for the date — after the Pliable migration the
     * balance-events stream can lag the bets stream by weeks, and on a day inside that gap every
     * player's deposit and withdrawal reads as `0`. That is the difference between "nobody deposited"
     * and "we do not yet know what anyone did with their money", and the owner has to be told which one
     * they are looking at. When `false`, every player's `status` is `data_pending` and the deposit and
     * withdrawal columns are not to be trusted as zero. When `true`, the day's money movements are real.
     */
    depositDataAvailable: z.boolean(),
});
//# sourceMappingURL=owner-report.js.map